import Button from '@sportnet/ui/Button';
import Modal, { ModalActions, ModalContent } from '@sportnet/ui/Modal';
import Segment from '@sportnet/ui/Segment';
import SegmentHeader from '@sportnet/ui/Segment/Header';
import { Table, Td, Th, Thead, Tr } from '@sportnet/ui/Table';
import AppContext from '@sportnet/ui/TheLayout/AppContext';
import Tabber from '@sportnet/ui/Tabber';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import Api from '../../Api';
import Layout from '../../components/Layout';
import { __ } from '../../utilities';

const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  color: white;
  background-color: ${({ status }) => {
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'pending':
        return '#ffc107';
      case 'submitted':
        return '#17a2b8';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  }};
`;

const ErrorMessage = styled.span`
  color: #dc3545;
`;

const RowGroup = styled.tbody`
  cursor: pointer;
  &:hover tr {
    background-color: #f5f5f5;
  }
`;

const MainRow = styled(Tr)``;

const ErrorTd = styled(Td)`
  color: #dc3545;
  font-size: 13px;
  padding: 4px 8px 12px 8px !important;
  border-top: none !important;
`;

const JsonPre = styled.pre`
  background-color: #1e1e1e;
  padding: 16px;
  border-radius: 4px;
  overflow: auto;
  font-size: 12px;
  font-family: 'Monaco', 'Menlo', 'Consolas', monospace;
  margin: 0;
  color: #d4d4d4;
  line-height: 1.5;
  max-height: 400px;
`;

const JsonKey = styled.span`
  color: #9cdcfe;
`;

const JsonString = styled.span`
  color: #ce9178;
`;

const JsonNumber = styled.span`
  color: #b5cea8;
`;

const JsonBoolean = styled.span`
  color: #569cd6;
`;

const JsonNull = styled.span`
  color: #569cd6;
`;

const FilterWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px;
`;

const FilterLabel = styled.span`
  font-weight: bold;
  margin-right: 8px;
`;

const FilterButton = styled.button`
  display: inline-block;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;
  color: ${({ active }) => (active ? 'white' : '#333')};
  background-color: ${({ active, status }) => {
    if (!active) return '#e0e0e0';
    switch (status) {
      case 'completed':
        return '#28a745';
      case 'pending':
        return '#ffc107';
      case 'submitted':
        return '#17a2b8';
      case 'failed':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  }};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.8;
  }
`;

const ObjectLink = styled(Link)`
  color: #007bff;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const BatchAccordion = styled.div`
  margin-bottom: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
`;

const BatchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background-color: #f5f5f5;
  cursor: pointer;
  &:hover {
    background-color: #ebebeb;
  }
`;

const BatchTitle = styled.span`
  font-weight: bold;
`;

const BatchContent = styled.div`
  padding: 16px;
  border-top: 1px solid #e0e0e0;
`;

const TABS = {
  users: 'users',
  organizations: 'organization_profiles',
};

const PAGE_SIZE = 100;

const NscSyncStatusPage = () => {
  const { appspace } = useParams();
  const location = useLocation();
  const history = useHistory();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const getInitialTab = () => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'organizations') return TABS.organizations;
    return TABS.users;
  };

  const getInitialStatus = () => {
    const statusParam = searchParams.get('status');
    if (statusParam === 'all') return '';
    if (['failed', 'pending', 'submitted', 'completed'].includes(statusParam)) {
      return statusParam;
    }
    return '';
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [nextId, setNextId] = useState(null);
  const [statusFilter, setStatusFilter] = useState(getInitialStatus);
  const [selectedItem, setSelectedItem] = useState(null);
  const [expandedBatchIndex, setExpandedBatchIndex] = useState(0);

  // Sync state changes to URL
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab === TABS.organizations) {
      params.set('tab', 'organizations');
    } else {
      params.set('tab', 'users');
    }
    params.set('status', statusFilter || 'all');
    history.replace({ search: params.toString() });
  }, [activeTab, statusFilter, history]);

  const loadData = useCallback(
    async (isLoadMore = false, currentNextId = null) => {
      setIsLoading(true);
      setError(null);
      try {
        const params = {
          limit: PAGE_SIZE,
        };
        if (currentNextId) {
          params.nextId = currentNextId;
        }
        if (statusFilter) {
          params.status = statusFilter;
        }
        const response = await Api.crmIssSyncStatusByCollection(
          appspace,
          activeTab,
          params,
        );
        if (isLoadMore) {
          setData(prev => [...prev, ...(response.items || [])]);
        } else {
          setData(response.items || []);
        }
        setNextId(response.nextId || null);
      } catch (e) {
        if (e.status === 404) {
          setError(__('Dáta neboli nájdené.'));
        } else {
          setError(
            e.details?.userinfo || __('Pri získavaní dát nastala chyba.'),
          );
        }
        if (!isLoadMore) {
          setData([]);
        }
        setNextId(null);
      } finally {
        setIsLoading(false);
      }
    },
    [appspace, activeTab, statusFilter],
  );

  useEffect(() => {
    setNextId(null);
    loadData(false, null);
  }, [activeTab, statusFilter, appspace, loadData]);

  const formatDate = dateString => {
    if (!dateString) return '-';
    return moment(dateString).format('DD.MM.YYYY HH:mm:ss');
  };

  const renderJsonValue = (value, indent = 0) => {
    const spaces = '  '.repeat(indent);

    if (value === null) {
      return <JsonNull>null</JsonNull>;
    }

    if (typeof value === 'boolean') {
      return <JsonBoolean>{value.toString()}</JsonBoolean>;
    }

    if (typeof value === 'number') {
      return <JsonNumber>{value}</JsonNumber>;
    }

    if (typeof value === 'string') {
      return <JsonString>"{value}"</JsonString>;
    }

    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return (
        <>
          {'[\n'}
          {value.map((item, i) => (
            <React.Fragment key={i}>
              {spaces} {renderJsonValue(item, indent + 1)}
              {i < value.length - 1 ? ',\n' : '\n'}
            </React.Fragment>
          ))}
          {spaces}
          {']'}
        </>
      );
    }

    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) return '{}';
      return (
        <>
          {'{\n'}
          {keys.map((key, i) => (
            <React.Fragment key={key}>
              {spaces} <JsonKey>"{key}"</JsonKey>:{' '}
              {renderJsonValue(value[key], indent + 1)}
              {i < keys.length - 1 ? ',\n' : '\n'}
            </React.Fragment>
          ))}
          {spaces}
          {'}'}
        </>
      );
    }

    return String(value);
  };

  const handleLoadMore = () => {
    if (nextId) {
      loadData(true, nextId);
    }
  };

  const handleRowClick = item => {
    setSelectedItem(item);
    setExpandedBatchIndex(0);
  };

  const handleCloseModal = () => {
    setSelectedItem(null);
    setExpandedBatchIndex(0);
  };

  const handleBatchClick = index => {
    setExpandedBatchIndex(expandedBatchIndex === index ? -1 : index);
  };

  const getSubmittedAt = item => {
    // submitted_at can be at item level or in the first (newest) batch
    if (item.submitted_at) return item.submitted_at;

    if (item.batches && item.batches.length > 0) {
      const newestBatch = item.batches[0];
      return newestBatch.submitted_at;
    }

    return null;
  };

  const getDisplayName = item => {
    // iss_request can be at item level or in the first (newest) batch
    let issRequest = item.iss_request;

    // If not at item level, try to get it from the first (newest) batch
    if (!issRequest && item.batches && item.batches.length > 0) {
      const newestBatch = item.batches[0];
      issRequest = newestBatch.iss_request;
    }

    if (!issRequest) return null;

    if (activeTab === TABS.users) {
      // For users, combine DegreePrefix, GivenName, FamilyName
      const parts = [
        issRequest.DegreePrefix,
        issRequest.GivenName,
        issRequest.FamilyName,
      ].filter(Boolean);
      return parts.length > 0 ? parts.join(' ') : null;
    } else {
      // For organizations, use CompanyTitle
      return issRequest.CompanyTitle || null;
    }
  };

  const renderObjectId = item => {
    if (activeTab === TABS.users && item.object_id) {
      return (
        <ObjectLink
          to={`/admin/${appspace}/users/${item.object_id}/profile`}
          onClick={e => e.stopPropagation()}
        >
          {item.object_id}
        </ObjectLink>
      );
    }

    if (activeTab === TABS.organizations && item.object_id) {
      return (
        <ObjectLink
          to={`/admin/${appspace}/ppo-redirect/${item.object_id}`}
          onClick={e => e.stopPropagation()}
        >
          {item.object_id}
        </ObjectLink>
      );
    }

    return item.object_id || '-';
  };

  const renderTable = () => {
    if (error) {
      return <ErrorMessage>{error}</ErrorMessage>;
    }

    if (!data || data.length === 0) {
      return <div>{__('Žiadne záznamy')}</div>;
    }

    return (
      <>
        <Table>
          <Thead>
            <Tr>
              <Th style={{ width: 100 }}>{__('Stav')}</Th>
              <Th>{__('Object ID')}</Th>
              <Th>{__('Meno / Názov')}</Th>
              <Th style={{ width: 150 }}>{__('Odoslané')}</Th>
              <Th style={{ width: 150 }}>{__('Posledná zmena')}</Th>
            </Tr>
          </Thead>
          {data.map((item, index) => (
            <RowGroup
              key={item._id || item.object_id || index}
              onClick={() => handleRowClick(item)}
            >
              <MainRow>
                <Td>
                  <StatusBadge status={item.current_status}>
                    {item.current_status || '-'}
                  </StatusBadge>
                </Td>
                <Td>{renderObjectId(item)}</Td>
                <Td>{getDisplayName(item) || '-'}</Td>
                <Td>{formatDate(getSubmittedAt(item))}</Td>
                <Td>{formatDate(item.last_updated)}</Td>
              </MainRow>
              {item.current_error && (
                <Tr>
                  <Td style={{ borderTop: 'none' }} />
                  <ErrorTd colSpan={4}>{item.current_error}</ErrorTd>
                </Tr>
              )}
            </RowGroup>
          ))}
        </Table>
        {nextId && (
          <div style={{ textAlign: 'center', marginTop: 16 }}>
            <Button onClick={handleLoadMore} disabled={isLoading}>
              {isLoading ? __('Načítavam...') : __('Načítať viac')}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderBatches = () => {
    if (
      !selectedItem ||
      !selectedItem.batches ||
      selectedItem.batches.length === 0
    ) {
      return <div>{__('Žiadne batches')}</div>;
    }

    return selectedItem.batches.map((batch, index) => (
      <BatchAccordion key={batch.batch_id || index}>
        <BatchHeader onClick={() => handleBatchClick(index)}>
          <BatchTitle>
            {__('Batch')} #{index + 1} - {batch.batch_id || '-'}
          </BatchTitle>
          <StatusBadge status={batch.status}>{batch.status || '-'}</StatusBadge>
        </BatchHeader>
        {expandedBatchIndex === index && (
          <BatchContent>
            <JsonPre>{renderJsonValue(batch)}</JsonPre>
          </BatchContent>
        )}
      </BatchAccordion>
    ));
  };

  return (
    <Layout>
      <AppContext
        title={__('Národné športové centrum - stav synchronizácie')}
        breadcrumbs={[
          {
            name: __('NŠC stav synchronizácie'),
            url: `/admin/${appspace}/nsc-sync-status`,
          },
        ]}
      />
      <Tabber
        active={activeTab === TABS.users ? __('Osoby') : __('Organizácie')}
        loading={isLoading}
        items={[__('Osoby'), __('Organizácie')]}
        onClickItem={itemName => {
          if (itemName === __('Osoby')) {
            setActiveTab(TABS.users);
          } else if (itemName === __('Organizácie')) {
            setActiveTab(TABS.organizations);
          }
        }}
      >
        <Segment>
          <Segment raised>
            <FilterWrapper>
              <FilterLabel>Stav synchronizácie:</FilterLabel>
              <FilterButton
                active={statusFilter === ''}
                status="all"
                onClick={() => setStatusFilter('')}
              >
                all
              </FilterButton>
              <FilterButton
                active={statusFilter === 'failed'}
                status="failed"
                onClick={() => setStatusFilter('failed')}
              >
                failed
              </FilterButton>
              <FilterButton
                active={statusFilter === 'pending'}
                status="pending"
                onClick={() => setStatusFilter('pending')}
              >
                pending
              </FilterButton>
              <FilterButton
                active={statusFilter === 'submitted'}
                status="submitted"
                onClick={() => setStatusFilter('submitted')}
              >
                submitted
              </FilterButton>
              <FilterButton
                active={statusFilter === 'completed'}
                status="completed"
                onClick={() => setStatusFilter('completed')}
              >
                completed
              </FilterButton>
            </FilterWrapper>

            {renderTable()}
          </Segment>
        </Segment>
      </Tabber>

      <Modal isOpen={!!selectedItem} handleClose={handleCloseModal}>
        <ModalContent>
          <SegmentHeader size="s">{__('Detail - Batches')}</SegmentHeader>
          {renderBatches()}
        </ModalContent>
        <ModalActions style={{ justifyContent: 'flex-end' }}>
          <Button primary onClick={handleCloseModal}>
            {__('Zavrieť')}
          </Button>
        </ModalActions>
      </Modal>
    </Layout>
  );
};

export default NscSyncStatusPage;
